import * as React from 'react'
import { Col, Row } from 'reactstrap';
import { AccentToolbar } from '../controls/AccentToolbar';
import { clearEntities, saveEntities } from '../services/DataService';
import { accentUtils, formHelper, goBack, goTo, setToolbarActionState, showOK, showWait, t, toolbarActionStates } from '../services/HelperService';
import InProgressOverlay from './InProgressOverlay';
import { getAccess } from '../services/UserService';
import { showAuditLog } from './AuditLogDlg';
import { showRecordInfo } from './RecordInfoDlg';

/**
 * Creates a configuration object for an audit history button.
 * (Same as previous version)
 */
const createAuditButtonConfig = ({
    isMobile,
    isEnabled,
    defaultText,
    baseId,
    tagType,
    title,
    idSuffix,
    originalData
}) => {
    const buttonText = accentUtils.isEmpty(title) ? defaultText : `${defaultText} ${title}`;
    const buttonId = `${baseId}${tagType}${idSuffix}`;
    const config = {
        type: 'button',
        text: isMobile ? "" : buttonText,
        id: buttonId,
        enable: isEnabled,
        icon: "clock",
        overflow: "always",
    };
    if (originalData) {
        config.originalQueryItem = originalData;
    }
    return config;
};

/**
 * Generates an array of audit button configurations based on the provided query.
 *
 * @param {object|array|null|undefined} query - The auditHistoryQuery prop value.
 * @param {object} commonParams - An object containing common button parameters.
 * @param {boolean} commonParams.isMobile - Whether the display is mobile.
 * @param {boolean} commonParams.isEnabled - Whether the buttons should be enabled.
 * @param {string} commonParams.defaultText - The default text for the buttons.
 * @param {string} commonParams.baseId - The base part of the button IDs.
 * @param {string} tagType - A tag/type string to incorporate into the IDs.
 * @returns {array} An array of button configuration objects. Can be empty.
 */
const generateAuditButtonConfigs = (query, commonParams, tagType) => {
    const generatedButtons = []; // Initialize an empty array

    if (!query) {
        return generatedButtons; // Return empty if query is null/undefined
    }

    if (Array.isArray(query)) {
        // Handle Array
        query.forEach((item, index) => {
            if (item && typeof item === 'object') {
                generatedButtons.push(createAuditButtonConfig({
                    ...commonParams,
                    title: item.title,
                    idSuffix: `_${index}`, // Unique suffix for arrays
                    originalData: item,
                    tagType: tagType
                }));
            } else {
                console.warn("Audit history query array item is not an object at index " + index + ":", item);
            }
        });
    } else if (typeof query === 'object') { // Check if it's a non-null object
        // Handle Single Object (Non-Array)
        generatedButtons.push(createAuditButtonConfig({
            ...commonParams,
            // title: undefined, // No specific title, uses defaultText
            idSuffix: '',      // No suffix needed for single button ID
            originalData: query,
            tagType: tagType
        }));
    } else {
        // Optional: Log if the query is of an unexpected type
        console.warn("Unexpected type for auditHistoryQuery:", typeof query, query);
    }

    return generatedButtons; // Return the array (might be empty, have 1, or multiple items)
};

/**
 * Creates a configuration object for a record info button.
 */
const createRecordInfoButtonConfig = ({
    isMobile,
    isEnabled,
    defaultText,
    baseId,
    tagType,
    title,
    idSuffix
}) => {
    const buttonText = accentUtils.isEmpty(title) ? defaultText : `${defaultText} ${title}`;
    const buttonId = `${baseId}${tagType}${idSuffix}`;
    return {
        type: 'button',
        text: isMobile ? "" : buttonText,
        id: buttonId,
        enable: isEnabled,
        icon: "info",
        overflow: "always",
    };
};

/**
 * Generates an array of record info button configurations based on the provided details.
 *
 * @param {object|array|null|undefined} details - The entityDetailsQuery prop value (already evaluated from viewData).
 * @param {object} commonParams - An object containing common button parameters.
 * @param {string} tagType - A tag/type string to incorporate into the IDs.
 * @returns {array} An array of button configuration objects. Can be empty.
 */
const generateRecordInfoButtonConfigs = (details, commonParams, tagType) => {
    const generatedButtons = [];

    if (!details) return generatedButtons;

    if (Array.isArray(details)) {
        details.forEach((item, index) => {
            const title = (item && typeof item === 'object') ? item.title : undefined;
            generatedButtons.push(createRecordInfoButtonConfig({
                ...commonParams,
                title,
                idSuffix: `_${index}`,
                tagType
            }));
        });
    } else {
        generatedButtons.push(createRecordInfoButtonConfig({
            ...commonParams,
            idSuffix: '',
            tagType
        }));
    }

    return generatedButtons;
};

class ViewToolbar extends React.Component {

    constructor(props) {

        super(props);



        this.delayAmount = 500;


        
        

        this.state = {
            hide: false
        };

        this.setDirty = this.setDirty.bind(this);
        this.setUnchangedState = this.setUnchangedState.bind(this);
        this.show = this.show.bind(this);
        this.hide = this.hide.bind(this);
        this.saveClicked = this.saveClicked.bind(this);
        this.closeClicked = this.closeClicked.bind(this);
        this.undoClicked = this.undoClicked.bind(this);
        this.redrawDelayed = this.redrawDelayed.bind(this);
        this.redraw = this.redraw.bind(this);
        this.toolBarItemClicked = this.toolBarItemClicked.bind(this);
        this.getToolbarItems = this.getToolbarItems.bind(this);
    }

    setDirty() {
        this.redrawDelayed();

    }

    setUnchangedState() {
        this.setState({});
    }

    show() {
        this.setState({ hide: false });
    }

    hide() {
        this.setState({ hide: true });
    }



    saveClicked() {


        return new Promise(p => {


            setToolbarActionState(toolbarActionStates.saving);



            this.props.saveView({ refreshOnSaved: true, navigating: false }).then(r => {
                p();

            }).catch((e) => {
                showOK("application_strings.application.dialogs.toolbarSaveErrorTitle", e, [], true, null, false, 800).then(() => {
                    p();
                });
            });

        });

        
    }


    closeClicked() {
        setToolbarActionState(toolbarActionStates.closing);
        return goBack();
    }

    undoClicked() {
        setToolbarActionState(toolbarActionStates.undoing);

        this.props.refresh();        
    }


    redrawDelayed() {
        var me = this;

        window.setTimeout(function () {
            me.setState({});
        }, me.delayAmount);
    }


    redraw() {
        this.setState({});
    }

    async toolBarItemClicked(e) {


        var args = {
            id: e.id,
            handled: false
        };

        this.props.toolbarItemClicked(args);


        if (args.handled)
            return;

        await this.props.beforeSaveCloseUndoClicked();

        if (args.id === "entityToolbarUndoBtn" || args.id === "entityToolbarUndoBtn_Top") {
            this.undoClicked();
        } else if (args.id === "entityToolbarCloseBtn" || args.id === "entityToolbarCloseBtn_Top") {
            await this.closeClicked();
        } else if (args.id === "entityToolbarSaveBtn" || args.id === "entityToolbarSaveBtn_Top") {
            await this.saveClicked();
        } else if (args.id && args.id.startsWith("entityToolbarHistoryBtn")) {
            // Check if the query data exists
            if (this.props.auditHistoryQuery) {
                const query = this.props.auditHistoryQuery;
                let queryToShow = query; // Default to using the whole query prop
                let extractedIndex = -1; // Default to an invalid index

                // --- Logic to extract index from ID ---
                const lastUnderscoreIndex = args.id.lastIndexOf('_');

                // Check if an underscore exists and it's not the last character
                if (lastUnderscoreIndex > -1 && lastUnderscoreIndex < args.id.length - 1) {
                    const indexString = args.id.substring(lastUnderscoreIndex + 1);
                    // Try to parse the part after the last underscore as an integer
                    const potentialIndex = parseInt(indexString, 10);

                    // Check if parsing resulted in a valid non-negative integer
                    if (!isNaN(potentialIndex) && potentialIndex >= 0) {
                        extractedIndex = potentialIndex;
                    }
                }
                // --- End of index extraction logic ---


                // --- Determine which query data to show ---
                // Check if the original query is an array AND we extracted a valid index
                if (Array.isArray(query) && extractedIndex !== -1) {
                    // Check if the index is within the bounds of the array
                    if (extractedIndex < query.length) {
                        // Select the specific item from the array
                        queryToShow = query[extractedIndex];
                        console.log(`Audit button clicked: Using query item at index ${extractedIndex}`); // Optional logging
                    } else {
                        // Index out of bounds - log a warning and fall back to default?
                        console.warn(`Audit button ID ${args.id} has index ${extractedIndex}, which is out of bounds for the query array (length ${query.length}).`);
                        // Keep queryToShow as the full array, or maybe show an error?
                        // Depending on requirements, you might want to prevent the dialog
                        // by setting queryToShow = null; or just proceed with the full array.
                        // For now, it falls back to showing logs for the full array/object.
                    }
                } else if (extractedIndex !== -1 && !Array.isArray(query)) {
                    // Optional: Log if an index was found but the query isn't an array
                    console.warn(`Audit button ID ${args.id} has index ${extractedIndex}, but the auditHistoryQuery is not an array.`);
                    // Falls back to using the entire query object as queryToShow
                }
                // If it's not an array, or if no valid index was extracted,
                // queryToShow remains the original this.props.auditHistoryQuery

                // --- Show the audit log with the determined data ---
                // Ensure we actually have something valid to show
                if (queryToShow) {
                    await showAuditLog(queryToShow);
                } else {
                    console.error("Could not determine valid audit query data to show for button ID:", args.id);
                    // Optionally show a user-friendly error message here
                }

            } else {
                console.warn("Audit button clicked, but this.props.auditHistoryQuery is missing.");
                // Optionally show an error message to the user
            }
        } else if (args.id && args.id.startsWith("entityToolbarRecordInfoBtn")) {
            const details = this.props.entityDetailsQuery;
            let detailsToShow = details;
            let extractedIndex = -1;

            const lastUnderscoreIndex = args.id.lastIndexOf('_');

            if (lastUnderscoreIndex > -1 && lastUnderscoreIndex < args.id.length - 1) {
                const indexString = args.id.substring(lastUnderscoreIndex + 1);
                const potentialIndex = parseInt(indexString, 10);
                if (!isNaN(potentialIndex) && potentialIndex >= 0) {
                    extractedIndex = potentialIndex;
                }
            }

            if (Array.isArray(details) && extractedIndex !== -1) {
                if (extractedIndex < details.length) {
                    detailsToShow = details[extractedIndex];
                } else {
                    console.warn(`Record Info button ID ${args.id} has index ${extractedIndex}, which is out of bounds for the details array (length ${details.length}).`);
                }
            }

            if (detailsToShow) {
                await showRecordInfo(detailsToShow);
            } else {
                console.warn("Record Info button clicked, but this.props.entityDetailsQuery is missing.");
            }
        } else {

        }
        
        await this.props.afterSaveCloseUndoClicked();
    }



    getToolbarItems() {

        var tagType = (this.props.top) ? "_Top" : "";

        var viewActions = this.props.toolbarActions(this.props.top, (this.props.top) ? "actionsBtn_Top" : "actionsBtn");

        if (viewActions == null)
            return null;


        let res = [];

        if (!this.props.hideSave) {
            res.push({ type: 'button', text: this.props.display.mobile ? "" : t("application_strings.application.buttons.save"), id: "entityToolbarSaveBtn" + tagType, enable: this.props.viewState.isDirty, icon: "save", overflow: "never" });
        }

        res.push({ type: 'button', text: this.props.display.mobile ? "" : t("application_strings.application.buttons.close"), id: "entityToolbarCloseBtn" + tagType, enable: true, icon: "close", overflow: "never" });
            
        if (!this.props.hideUndo) {
            res.push({ type: 'button', text: this.props.display.mobile ? "" : t("application_strings.application.buttons.undo"), id: "entityToolbarUndoBtn" + tagType, enable: this.props.viewState.isDirty, icon: "undo", overflow: "never" });
        }




        if (!accentUtils.isNull(viewActions)) {


            if (viewActions.length > 0) {
                var activeOnlyItems = [];

                viewActions.forEach(function (i) {
                    if (!accentUtils.isNull(i.enable) && i.enable) {
                        activeOnlyItems.push(i);
                    }

                    if (i.type == 'separator') {
                        if (activeOnlyItems.length > 0) {
                            if (activeOnlyItems[activeOnlyItems.length - 1].type != 'separator') {
                                activeOnlyItems.push(i);
                            }
                        }
                    }


                });

                if (activeOnlyItems.length > 0 && activeOnlyItems[activeOnlyItems.length - 1].type == 'separator') {
                    activeOnlyItems.splice(-1, 1);
                }

                viewActions = activeOnlyItems;
            }




            if (viewActions.length > 0)
                res.push({ type: 'separator' });
            res = res.concat(viewActions);
        }

        const { auditHistoryQuery, display, viewState, entityDetailsQuery } = this.props;

        if (auditHistoryQuery && (getAccess().features().FeatureAllowManagement() || getAccess().features().FeatureAllowSetup())) {

            // --- Define common parameters using 'this.props' ---
            const commonButtonParams = {
                isMobile: display.mobile,         // Use this.props.display
                isEnabled: !viewState.isDirty,     // Use this.props.viewState
                defaultText: t("application_strings.application.buttons.auditLog"),
                baseId: "entityToolbarHistoryBtn"
                // tagType is passed separately below
            };
            // --- End of common parameters ---

            // Generate the button configurations by calling the standalone function
            const auditButtonsToAdd = generateAuditButtonConfigs(
                auditHistoryQuery, // Pass the prop value
                commonButtonParams,
                tagType            // Pass tagType (assuming it's available)
            );

            // Use the spread operator (...) to push all generated buttons into the result array
            if (auditButtonsToAdd.length > 0) {
                res.push(...auditButtonsToAdd);
            }
        }

        if (entityDetailsQuery) {
            const commonButtonParams = {
                isMobile: display.mobile,
                isEnabled: true,
                defaultText: t("application_strings.application.buttons.recordInfo"),
                baseId: "entityToolbarRecordInfoBtn"
                // tagType is passed separately below
            };

            const recordInfoButtonsToAdd = generateRecordInfoButtonConfigs(
                entityDetailsQuery,
                commonButtonParams,
                tagType
            );

            if (recordInfoButtonsToAdd.length > 0) {
                res.push(...recordInfoButtonsToAdd);
            }
        }



        return res;
    }

    render() {


        const hideOther = this.props.display.mobile;

        const toolbarclass = "float-right accentViewToolBar";


        const tagType = (this.props.top) ? "_Top" : "";
        
        return (<div>
            {!this.state.hide && <AccentToolbar
                className={toolbarclass}
                getToolbarItems={this.getToolbarItems}
                onClick={this.toolBarItemClicked}
                toolBarName={"entityToolbar" + tagType}
                hideAll={hideOther}
                display={ this.props.display}
            />}
        </div>
        );
    }
}



export class ViewWithToolbars extends React.Component {

    constructor(props) {
        super(props);

        this.toolBar = React.createRef();
        this.toolBarBottom = React.createRef();

        const params = this.props?.view?.props?.params;

        this.auditHistoryQuery = (accentUtils.isNull(this.props.auditHistoryQuery) || accentUtils.isNull(params)) ? null : this.props.auditHistoryQuery(params);

        this.state = {
            actionClicked: false
        };

        this.toolbarItemClicked = this.toolbarItemClicked.bind(this);
        this.getToolbarActions = this.getToolbarActions.bind(this);
        this.componentDidUpdate = this.componentDidUpdate.bind(this);
        this.componentDidMount = this.componentDidMount.bind(this);
        this.refreshToolbar = this.refreshToolbar.bind(this);
        this.beforeSaveCloseUndoClicked = this.beforeSaveCloseUndoClicked.bind(this);
        this.afterSaveCloseUndoClicked = this.afterSaveCloseUndoClicked.bind(this);
    }

    componentDidUpdate() {
        this.refreshToolbar();
    }
    componentDidMount() {
        this.refreshToolbar();
    }
    refreshToolbar() {
        var me = this;
        window.setTimeout(function () {

            if (!accentUtils.isNull(me.toolBar.current))
                me.toolBar.current.redraw();
            if (!accentUtils.isNull(me.toolBarBottom.current))
                me.toolBarBottom.current.redraw();

        }, 350);

    }

    beforeSaveCloseUndoClicked() {

        return new Promise(p => {
            this.setState({ actionClicked: true }, () => {
                p();
            });
        });

    }

    afterSaveCloseUndoClicked() {
        return new Promise(p => {
            this.setState({ actionClicked: false }, () => {
                p();
            });
        });

    }


    toolbarItemClicked(e) {

        if (accentUtils.isNull(this.props.viewRef.current)) {
            return null;
        }

        if (!accentUtils.isNull(this.props.viewRef.current) && !accentUtils.isNull(this.props.viewRef.current.toolbarItemClicked))
            return this.props.viewRef.current.toolbarItemClicked(e);

    }

    getToolbarActions(isTop, actionsTagName) {

        if (accentUtils.isNull(this.props.viewRef.current)) {
            return null;
        }

        if (!accentUtils.isNull(this.props.viewRef.current) && !accentUtils.isNull(this.props.viewRef.current.getToolbarActions))
            return this.props.viewRef.current.getToolbarActions(isTop, actionsTagName);
        return [];
    }




    render() {


        var viewClassName = undefined;

        if (this.props.viewState.isReadOnly) {
            viewClassName = "acc-readonly-view";
        }

        return (
            <Col md={12}>
                <InProgressOverlay text="Please wait" block={this.state.actionClicked} showText={this.state.actionClicked}>
                    <ViewToolbar
                        ref={this.toolBar}
                        top={true}
                        toolbarActions={this.getToolbarActions}
                        toolbarItemClicked={this.toolbarItemClicked}
                        refresh={this.props.refresh}
                        viewState={this.props.viewState}
                        display={this.props.display}
                        navigate={this.props.navigate}
                        saveView={this.props.saveView}
                        beforeSaveCloseUndoClicked={this.beforeSaveCloseUndoClicked}
                        afterSaveCloseUndoClicked={this.afterSaveCloseUndoClicked}
                        hideSave={this.props.hideSave}
                        hideUndo={this.props.hideUndo}
                        auditHistoryQuery={this.auditHistoryQuery}
                        entityDetailsQuery={this.props.entityDetailsQuery}
                    />
                    <Row className={viewClassName} style={{ paddingTop: "10px", paddingBottom: "10px" }} >
                        {this.props.view}
                    </Row>
                    <ViewToolbar
                        ref={this.toolBarBottom}
                        top={false}
                        toolbarActions={this.getToolbarActions}
                        toolbarItemClicked={this.toolbarItemClicked}
                        refresh={this.props.refresh}
                        viewState={this.props.viewState}
                        display={this.props.display}
                        navigate={this.props.navigate}
                        saveView={this.props.saveView}
                        beforeSaveCloseUndoClicked={this.beforeSaveCloseUndoClicked}
                        afterSaveCloseUndoClicked={this.afterSaveCloseUndoClicked}
                        hideSave={this.props.hideSave}
                        hideUndo={this.props.hideUndo}
                        auditHistoryQuery={this.auditHistoryQuery}
                        entityDetailsQuery={this.props.entityDetailsQuery}
                    />
                </InProgressOverlay >
            </Col>

        );
    }
}
